/**
 * The masthead (D132): the game's name on the door, always in type — with
 * its own mark above it when the pack declares one — and, before there is a
 * game, the engine's.
 *
 * @remarks
 * The name is VISIBLE TEXT everywhere, never hidden behind a picture. The
 * first cut of D132 let a logo replace the typeset name, and Mike found the
 * game's title reduced to a small line under a giant engine lockup: a title
 * screen's one job is to name the game, and a mark is an addition to that,
 * not a substitute for it. So the heading is the title, set at hero scale on
 * the title screen and at masthead scale in the scrollback; `logo` draws a
 * mark above it at masthead scale in both places; the subtitle sits under
 * the name. A pack that declares no logo is not missing one.
 *
 * The engine masthead exists for the two moments no game is present — the
 * boot and a failed boot. Those are the engine speaking about itself, so its
 * own mark is the honest one there; the moment a bundle loads, the game's
 * masthead replaces it.
 *
 * @packageDocumentation
 */

import type { GameDescription } from '@orpheus/api-contracts';

/** The engine's own lockup — an app asset, because the shell is the engine's. */
const ENGINE_WORDMARK_URL = '/brand/orpheus-logo-wordmark-transparent.svg';

/** Props for {@link GameMastheadComponent}. */
export interface GameMastheadComponentProps {
	/** The game's description — title, subtitle, and its logo already resolved (D156). */
	readonly game: GameDescription;
}

/**
 * Render the loaded game's masthead.
 *
 * @param props - {@link GameMastheadComponentProps}
 * @returns The masthead header
 */
export const GameMastheadComponent = ({ game }: GameMastheadComponentProps) => (
	<Masthead logoUrl={game.logo} name={game.title} subtitle={game.subtitle} />
);

/**
 * Render the engine's masthead — shown only while no game is loaded.
 *
 * @returns The masthead header
 */
export const EngineMastheadComponent = () => <Masthead logoUrl={ENGINE_WORDMARK_URL} name="ORPHEUS" subtitle={undefined} />;

/** Props for {@link Masthead}. */
interface MastheadProps {
	/** The mark's URL, when there is a mark to draw above the name. */
	readonly logoUrl: string | undefined;
	/** The name — the h1, always visible. */
	readonly name: string;
	/** The line under the name, when there is one. */
	readonly subtitle: string | undefined;
}

/**
 * The header itself: an optional mark, the name as the h1, an optional line.
 *
 * @remarks
 * The mark is decorative (`alt=""`): the h1 beside it already says the name,
 * and the mark is outside the heading so an h1 is never an image alone — an
 * h1 whose only child is a hidden image is an EMPTY HEADING, which axe caught
 * once under the old arrangement.
 *
 * @param props - {@link MastheadProps}
 * @returns The header
 */
const Masthead = ({ logoUrl, name, subtitle }: MastheadProps) => (
	<header className="player-shell__header">
		{logoUrl === undefined ? undefined : <img alt="" className="player-shell__mark" src={logoUrl} />}
		<h1 className="player-shell__wordmark">{name}</h1>
		{subtitle === undefined ? undefined : <p className="player-shell__subtitle">{subtitle}</p>}
	</header>
);

/**
 * The "powered by" line on the title screen: the engine's symbol and its
 * name, small, at the foot of the title content (Mike's ruling, 2026-09-02:
 * the credit carries the mark too — on a creator's game it is the one place
 * the engine appears).
 *
 * @returns The credit line
 */
export const EngineCreditComponent = () => (
	<p className="player-shell__credit">
		<img alt="" className="player-shell__credit-mark" src="/brand/orpheus-logo-transparent.svg" />
		<span>Powered by ORPHEUS</span>
	</p>
);
